import { useEffect, useRef } from 'react';

import type { Routine, Unsubscribe } from '@sb/remote-control/types';
import { onGetRoutine } from '@sbrc/services';

const PREFETCH_COUNT = 10;

/**
 * Module-level cache for prefetched routine data.
 * Survives component unmount so the data is available when
 * the user navigates to a routine page.
 */
const prefetchCache = new Map<string, Routine.ConvertedResponse>();
const activeSubscriptions = new Map<string, Unsubscribe>();
let cleanupTimer: ReturnType<typeof setTimeout> | null = null;

function cancelPendingCleanup() {
  if (cleanupTimer) {
    clearTimeout(cleanupTimer);
    cleanupTimer = null;
  }
}

function scheduleCleanup() {
  cancelPendingCleanup();

  // Keep subscriptions alive for 30s after the home page unmounts
  // so data is warm when the routine page loads.
  cleanupTimer = setTimeout(() => {
    for (const unsub of activeSubscriptions.values()) {
      unsub();
    }

    activeSubscriptions.clear();
    prefetchCache.clear();
    cleanupTimer = null;
  }, 30_000);
}

/**
 * Read a routine from the prefetch cache (if available).
 * Used by usePageRobotAndRoutine to seed initial state.
 */
export function getPrefetchedRoutine(
  routineID: string,
): Routine.ConvertedResponse | undefined {
  return prefetchCache.get(routineID);
}

/**
 * Prefetch full routine data for the first N routines in the list.
 *
 * Starts Feathers subscriptions so the data is cached by the time
 * the user taps a routine. Subscriptions are kept alive briefly
 * after unmount to survive page navigation.
 */
export function usePrefetchRoutines(routineIDs: string[]) {
  const prevIDsRef = useRef<string[]>([]);

  useEffect(() => {
    cancelPendingCleanup();

    const idsToPrefetch = routineIDs.slice(0, PREFETCH_COUNT);

    // Skip if the prefetch set hasn't changed
    if (
      idsToPrefetch.length === prevIDsRef.current.length &&
      idsToPrefetch.every((id, i) => id === prevIDsRef.current[i])
    ) {
      return;
    }

    prevIDsRef.current = idsToPrefetch;

    const idsToKeep = new Set(idsToPrefetch);

    // Unsubscribe from routines no longer in the top N
    for (const [id, unsub] of activeSubscriptions) {
      if (!idsToKeep.has(id)) {
        unsub();
        activeSubscriptions.delete(id);
        prefetchCache.delete(id);
      }
    }

    // Start subscriptions for new routines
    for (const id of idsToPrefetch) {
      if (!activeSubscriptions.has(id)) {
        const unsub = onGetRoutine(id, (routine) => {
          if (routine) {
            prefetchCache.set(id, routine);
          } else {
            // A deleted routine must not linger here: the routine page seeds
            // its initial state from this cache and would render the dead
            // routine for a frame before the subscription corrects it.
            prefetchCache.delete(id);
          }
        });

        activeSubscriptions.set(id, unsub);
      }
    }
  }, [routineIDs]);

  // On unmount, delay cleanup so data survives navigation
  useEffect(() => {
    return () => {
      scheduleCleanup();
    };
  }, []);
}
